import { useEffect, useMemo, useState } from 'react';
import {
  Box, Checkbox, Chip, Divider, FormControlLabel, IconButton, InputAdornment, Stack,
  TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { api } from '../api/client';
import type { Category, CategoryGroup } from '../api/types';

/**
 * The headings come from the database, not from here.
 *
 * They used to be two, hard-coded: "Infrastructure" meaning the safety and
 * civic groups, and "Points of interest" meaning everything else. That put
 * Office, Bike Hire, School, Embassy, Bank and Nursery in one list — four
 * different jobs under one word — and no operator could change it without a
 * release. Groups are rows now: named, ordered, and marked as infrastructure
 * or not, in Admin.
 */

interface Props {
  categories: (Category & { poiCount?: number })[];
  selected: string[];
  onChange: (categoryKeys: string[]) => void;
  /** Colour swatch per category, matching the dots drawn on the map. */
  colourFor: (categoryKey: string) => string;
}

export function CategoryPanel({ categories, selected, onChange, colourFor }: Props) {
  const [filter, setFilter] = useState('');
  const [groups, setGroups] = useState<CategoryGroup[]>([]);

  useEffect(() => {
    void api.categoryGroups().then(setGroups).catch(() => setGroups([]));
  }, []);

  const sections = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    /*
     * Infrastructure first, then everything else, each in the order Admin
     * gave it. A group with nothing in this city drops out below.
     */
    const ordered = [...groups].sort((a, b) =>
      Number(b.isInfrastructure) - Number(a.isInfrastructure)
      || a.sortOrder - b.sortOrder);
    return ordered.map((group) => ({
      title: group.label,
      items: categories
        .filter((c) => c.group === group.key && (c.poiCount ?? 0) > 0)
        /*
         * Matched on the key as well as the label, since the key is the name
         * OpenStreetMap uses — someone looking for "amenity" or "pharmacy"
         * should find it whichever vocabulary they think in.
         */
        .filter((c) => !needle
          || c.label.toLowerCase().includes(needle)
          || c.key.toLowerCase().includes(needle))
        .sort((a, b) => (b.poiCount ?? 0) - (a.poiCount ?? 0)),
    })).filter((section) => section.items.length > 0);
    // `groups` belongs here: they arrive from the API after the first render,
    // and without it the sections stayed as they were computed with none —
    // which is an empty panel, permanently.
  }, [categories, filter, groups]);

  const matchCount = sections.reduce((sum, section) => sum + section.items.length, 0);

  const toggle = (key: string) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  const toggleSection = (keys: string[]) => {
    const allOn = keys.every((k) => selected.includes(k));
    onChange(allOn ? selected.filter((k) => !keys.includes(k)) : [...new Set([...selected, ...keys])]);
  };

  /*
   * "Nothing imported" is about the city, not the search box.
   *
   * Judged on the unfiltered list: filtering to no matches used to hit this
   * and replace the whole panel — search field included — leaving no way to
   * clear the term that caused it.
   */
  const hasAnyCategories = categories.some((c) => (c.poiCount ?? 0) > 0);

  if (!hasAnyCategories) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ p: 2, display: 'block' }}>
        No places imported for this city yet.
      </Typography>
    );
  }

  return (
    <Stack sx={{ py: 1 }}>
      <Box sx={{ px: 1.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search categories"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: filter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilter('')} aria-label="Clear search">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Box>

      {filter && matchCount === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
          Nothing matches “{filter}”.
        </Typography>
      )}

      {sections.map((section, index) => {
        const keys = section.items.map((c) => c.key);
        const shown = keys.filter((k) => selected.includes(k)).length;

        return (
          <Box key={section.title}>
            {index > 0 && <Divider sx={{ my: 1 }} />}
            <Stack
              direction="row"
              spacing={1}
              onClick={() => toggleSection(keys)}
              sx={{
                alignItems: 'center', px: 2, py: 0.75, cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ flex: 1, letterSpacing: '0.09em', lineHeight: 1.6 }}
              >
                {section.title}
              </Typography>
              {/* While filtering, the count is of what is on screen — the
                  section toggle acts on the visible rows, not the hidden ones. */}
              {shown > 0 && (
                <Chip
                  size="small"
                  label={filter ? `${shown} of ${section.items.length}` : shown}
                  sx={{ height: 18, fontSize: 11 }}
                />
              )}
            </Stack>

            {section.items.map((category) => (
              <FormControlLabel
                key={category.key}
                onChange={() => toggle(category.key)}
                sx={{
                  display: 'flex', width: '100%', maxWidth: '100%', m: 0,
                  pl: 0.5, pr: 1.5, py: 0.1, minWidth: 0, overflow: 'hidden',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                control={<Checkbox size="small" checked={selected.includes(category.key)} />}
                label={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', width: '100%', minWidth: 0 }}
                  >
                    <Box
                      sx={{
                        width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                        bgcolor: colourFor(category.key),
                      }}
                    />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {category.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {category.poiCount}
                    </Typography>
                  </Stack>
                }
                slotProps={{ typography: { sx: { flex: 1, minWidth: 0 } } }}
              />
            ))}
          </Box>
        );
      })}
    </Stack>
  );
}
